import React, {useState, forwardRef, createRef, useEffect} from 'react'
import gql from 'graphql-tag'
import {Mutation} from 'react-apollo'
import {Spin} from 'antd'
import {PlusOutlined, DeleteFilled} from '@ant-design/icons'
import Button from 'components/shared/Button'
import FileUploader from 'lib/fileUploader'
import './index.scss'

const signFilestackEventMutation = gql`
  mutation SignFilestackEvent($input: SignFilestackEventInput!) {
    SignFilestackEvent(input: $input) {
      policy
      signature
    }
  }
`

const FileInputButton = forwardRef((props, _) => {
  const fileInputRef = createRef()
  const selectFile = () => fileInputRef.current.click()

  const {isUploading, ...rest} = props

  return (
    <div className="file-input-button">
      <span
        role="button"
        className="inner upload-button"
        onClick={selectFile}
      >
        {isUploading && (
          <Spin />
        )}

        {!isUploading && (
          <>
            <PlusOutlined />
            <span className="label">
              Last opp
            </span>
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          {...rest}
        />
      </span>
    </div>
  )
})

const UploadedFile = ({uploader, removeUpload}) => {
  const [preview, setPreview] = useState(null)

  useEffect(() => {
    const reader = new FileReader()
    reader.addEventListener('load', () => setPreview(reader.result))
    reader.readAsDataURL(uploader.inputBlob)
  })

  return (
    <div className="uploaded-file">
      <span className="inner">
        {!!preview && (
          <img src={preview} alt="" />
        )}
      </span>
      <span className="actions-overlay">
        <Button onClick={removeUpload}>
          <DeleteFilled />
        </Button>
      </span>
    </div>
  )
}

const AvatarUploader = forwardRef((props, _) => {
  const [uploader, setUploader] = useState(null)

  const isUploading = uploader && uploader.state === 'uploading'
  const isUploaded = uploader && uploader.state === 'uploaded'

  const getPolicy = (options) => {
    return props.signFilestackEvent({
      variables: {
        input: {
          options: JSON.stringify(options),
        }
      }
    }).then(({data}) => data.SignFilestackEvent)
  }

  const addUpload = (file) => {
    const fileUploader = new FileUploader({
      getPolicy,
      blob: file,
      fileTypes: ['image/jpeg'],
      onUpdate: setUploader,
      onSuccess: (uploader) => {
        if (props.onChange) {
          return props.onChange(uploader.uploadedBlob.url)
        }
      }
    })
    setUploader(fileUploader)
    fileUploader.startUpload()
  }

  const handleFileChange = (e) => {
    const files = e.target.files
    if (files && files.length) {
      addUpload(files[0])
    }
  }

  const removeUpload = () => {
    uploader.removeUpload()
    setUploader(null)
    return props.onChange(null)
  }

  return (
    <div className="avatar-uploader">
      {isUploaded && (
        <UploadedFile
          uploader={uploader}
          removeUpload={removeUpload}
        />
      )}

      {!isUploaded && (
        <FileInputButton
          accept="image/jpeg"
          multiple={false}
          onChange={handleFileChange}
          isUploading={isUploading}
        />
      )}
    </div>
  )
})

const WithMutation = forwardRef((props, ref) => (
  <Mutation mutation={signFilestackEventMutation}>
    {(signFilestackEvent) => (
      <AvatarUploader
        ref={ref}
        signFilestackEvent={signFilestackEvent}
        {...props}
      />
    )}
  </Mutation>
))

export default WithMutation
